import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CurrentScopeFacade,
  GlobalStoreService,
} from '@principle-theorem/ng-principle-shared';
import { TrackByFunctions } from '@principle-theorem/ng-shared';
import {
  IHealthcareClaim,
  IInvoice,
  IStaffer,
  type IPractice,
  type ITyroTerminal,
} from '@principle-theorem/principle-core/interfaces';
import { Invoice, Practice } from '@principle-theorem/principle-core';
import { type DocumentReference } from '@principle-theorem/shared';
import {
  isSameRef,
  multiFilter,
  sortTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import { type Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export interface ITerminalOption {
  practiceRef: DocumentReference<IPractice>;
  terminal: WithRef<ITyroTerminal>;
  isCurrentlyPaired: boolean;
}

export type IntegrationKeyFormData = Required<
  Pick<ITyroTerminal, 'integrationKey' | 'mid' | 'tid'>
>;

export interface ISelectedTerminal {
  practiceRef: DocumentReference<IPractice>;
  terminalData: IntegrationKeyFormData;
}

export interface ISelectTyroTerminalRequest {
  invoice: Pick<IInvoice, 'practice' | 'items' | 'claims'>;
  claim?: IHealthcareClaim;
}

@Component({
  selector: 'pr-select-tyro-terminal',
  templateUrl: './select-tyro-terminal.component.html',
  styleUrls: ['./select-tyro-terminal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectTyroTerminalComponent {
  options$: Observable<ITerminalOption[]>;
  terminals$: Observable<WithRef<ITyroTerminal>[]>;
  trackByOptions = TrackByFunctions.ref<ITerminalOption>('terminal.ref');
  practiceName$: Observable<string | undefined>;

  constructor(
    private _currentScopeFacade: CurrentScopeFacade,
    private _dialogRef: MatDialogRef<
      SelectTyroTerminalComponent,
      ISelectedTerminal
    >,
    private _globalStore: GlobalStoreService,
    @Inject(MAT_DIALOG_DATA)
    public data: ISelectTyroTerminalRequest
  ) {
    this.practiceName$ = this._currentScopeFacade.currentPractice$.pipe(
      map((practice) => practice?.name)
    );

    this.options$ = this._currentScopeFacade.currentPractice$.pipe(
      switchMap((practice) => this._getPracticeTerminals$(practice)),
      multiFilter((option) => !!option.terminal.integrationKey)
    );
  }

  select(option: ITerminalOption): void {
    this._dialogRef.close(this._toSelectedTerminal(option));
  }

  getStafferImage$(
    practitionerRef: DocumentReference<IStaffer>
  ): Observable<string | undefined> {
    return this._globalStore.getStafferImage$({ ref: practitionerRef });
  }

  getStafferName$(
    practitionerRef: DocumentReference<IStaffer>
  ): Observable<string> {
    return this._globalStore.getStafferName$({ ref: practitionerRef });
  }

  filterTerminals(
    terminals: WithRef<ITyroTerminal>[],
    invoice: Pick<IInvoice, 'practice' | 'items' | 'claims'>,
    claim?: IHealthcareClaim
  ): WithRef<ITyroTerminal>[] {
    if (terminals.length === 1) {
      return terminals;
    }
    const practitionersOnInvoice = claim
      ? [claim.practitioner]
      : Invoice.staffOnInvoice(invoice);

    return terminals.filter((terminal) => {
      const hasPracitionerOnInvoice = practitionersOnInvoice.some(
        (practitionerRef) =>
          isSameRef(practitionerRef, terminal.practitionerRef)
      );
      return !terminal.practitionerRef || hasPracitionerOnInvoice;
    });
  }

  private _getPracticeTerminals$(
    practice: WithRef<IPractice> | undefined
  ): Observable<ITerminalOption[]> {
    if (!practice) {
      return of([]);
    }
    return Practice.tyroTerminals$(practice).pipe(
      map((terminals) =>
        this.filterTerminals(terminals, this.data.invoice, this.data.claim)
      ),
      map((terminals) => this._toTerminalOptions(practice.ref, terminals))
    );
  }

  private _toTerminalOptions(
    practiceRef: DocumentReference<IPractice>,
    terminals: WithRef<ITyroTerminal>[]
  ): ITerminalOption[] {
    const ordered = terminals.sort((a, b) =>
      sortTimestamp(a.lastPaired, b.lastPaired)
    );
    const current = first(ordered);
    return ordered.map((terminal) => ({
      terminal,
      isCurrentlyPaired: isSameRef(current, terminal),
      practiceRef,
    }));
  }

  private _toSelectedTerminal(
    option: ITerminalOption
  ): ISelectedTerminal | undefined {
    if (!option.terminal.integrationKey) {
      return;
    }
    return {
      practiceRef: option.practiceRef,
      terminalData: {
        integrationKey: option.terminal.integrationKey,
        mid: option.terminal.mid,
        tid: option.terminal.tid,
      },
    };
  }
}
